import { useState, useEffect, useCallback, useRef } from 'react';
import { http } from '../lib/api.js';

/**
 * FE-17: "Data fetching lives in a shared hook. Components do not call Axios
 * directly."
 *
 * FE-9: every asynchronous view handles four states explicitly — loading, empty,
 * error (with retry) and content — so this hook returns all four rather than
 * just data.
 *
 * DB-5: there is deliberately no cache. Dashboard figures are money; a stale
 * number rendered from a cache is money reported wrongly.
 */
export function useResource(path, { params, enabled = true, transform } = {}) {
  const [state, setState] = useState({ data: null, error: null, loading: enabled });
  const [reloadToken, setReloadToken] = useState(0);
  const latestRequest = useRef(0);

  // Serialised so an inline object literal in a caller does not re-fire forever.
  const paramsKey = params ? JSON.stringify(params) : '';

  useEffect(() => {
    if (!enabled) {
      setState({ data: null, error: null, loading: false });
      return undefined;
    }

    const requestId = ++latestRequest.current;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    http
      .get(path, paramsKey ? { params: JSON.parse(paramsKey) } : undefined)
      .then((data) => {
        // An earlier request resolving after a later one must not overwrite it.
        if (cancelled || requestId !== latestRequest.current) return;
        setState({ data: transform ? transform(data) : data, error: null, loading: false });
      })
      .catch((error) => {
        if (cancelled || requestId !== latestRequest.current) return;
        setState({ data: null, error, loading: false });
      });

    return () => { cancelled = true; };
  }, [path, paramsKey, enabled, reloadToken]);

  const refetch = useCallback(() => setReloadToken((n) => n + 1), []);

  /** Lets a caller fold a mutation result in without a round trip. */
  const setData = useCallback((updater) => {
    setState((s) => ({ ...s, data: typeof updater === 'function' ? updater(s.data) : updater }));
  }, []);

  return { ...state, refetch, setData };
}

/**
 * FE-11: a submit button disables and shows a spinner while in flight, so double
 * submission is impossible from the UI. The `loading` flag returned here is what
 * drives that, and the in-flight guard makes it true even if a caller forgets.
 */
export function useMutation(mutator) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);

  /**
   * The `true` here is load-bearing, not redundant.
   *
   * React 18's StrictMode mounts, unmounts and remounts every component in
   * development. Without re-arming the ref on the second mount, the simulated
   * unmount left it permanently `false` — so `setLoading(false)` and `setError`
   * below never ran again, and every mutation in the app span forever and
   * swallowed its own error. It worked in a production build, which is exactly
   * why it survived so long.
   */
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const mutate = useCallback(
    async (...args) => {
      // A second call while one is in flight is a double-click, not an error —
      // but it must not leave the button spinning on a request it never made.
      if (inFlight.current) return undefined;
      inFlight.current = true;
      setLoading(true);
      setError(null);
      try {
        return await mutator(...args);
      } catch (err) {
        if (mounted.current) setError(err);
        throw err;
      } finally {
        inFlight.current = false;
        if (mounted.current) setLoading(false);
      }
    },
    [mutator],
  );

  return { mutate, loading, error, reset: () => setError(null) };
}

/**
 * RF-3: "Refunds accept an idempotency key. The dashboard generates one per
 * refund dialog." One per dialog, not one per click — that is what makes a
 * double-clicked confirm button safe.
 */
export function newIdempotencyKey(prefix = 'ui') {
  const random = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}_${random}`;
}
